import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl, useIntl } from 'react-intl';
import TaskRequiredAuthorizationsIcon from '@/modules/tasks/TaskRequiredAuthorizationsIcon';
import classNames from 'classnames';
import {
  FaTrashAlt,
  FaShareAlt,
  FaRegClock,
  FaExternalLinkAlt,
  FaRegWindowRestore,
} from 'react-icons/fa';
import intlShape from '@/shared/utils/intlShape';
import CanDelete from '@/shared/components/security/CanDelete';
import handleHttpError from '@/shared/services/HandleHttpError';
import moment from 'moment';

import Checkbox from '@/shared/components/input/Checkbox';

import RelationName from '@/shared/components/RelationName';
import FormattedListValue from '@/shared/components/FormattedListValue';

import { injectStateService, StateService } from '@/shared/services/StateService';
import momentDurationFormatSetup from 'moment-duration-format';
import { hasAssignments } from '@/shared/components/security/HasAssignments';
import { mapInvoiceStreamName } from '@/shared/services/ObjectDisplayService';
import EditableDatePicker from '@/shared/components/editable/EditableDatePicker';
import EditableUserSearch from '@/shared/components/editable/EditableUserSearch';
import DataCacheService from '@/shared/services/DataCacheService';
import TaskListActionsButton from './TaskListActionsButton';
import messages from '../taskMessages';
import TaskIcon from '../TaskIcon';
import TaskStatusToggle from '../TaskStatusToggle';
import FormattedDateShort from '../../../shared/components/FormattedDateShort';
import EditableNumber from '@/shared/components/editable/EditableNumber';

momentDurationFormatSetup(moment);

const getLinkIconTooltipMessage = (showLinkButton, taskLevel) => {
  if (showLinkButton) return messages.OpenInTaskboard;
  if (taskLevel === 2) return messages.AToDoCannotBeOpenedInTaskBoard;
  return messages.ThisTaskCannotBeOpenedInTaskBoard;
};

const TaskListRow = ({
  task,
  stateService,
  intl,
  handleTaskSelection,
  onAddRegistrationClick,
  onShowTaskDetails,
  onRequestDeleteTask,
  onTasksUpdated,
  onShareItem,
  onSendEmail,
  onUpdateUser,
  onUpdateDeadline,
  onUpdateExecutionDate,
  onUpdateExecutionDuration,
  columnStatus,
}) => {
  const { relation } = task;

  const modifiedBy = useMemo(() => DataCacheService.getUserName(task.modifiedBy), [
    task.modifiedBy,
  ]);
  const createdBy = useMemo(() => DataCacheService.getUserName(task.createdBy), [task.createdBy]);

  const formattedDuration = moment.duration(task.duration, 'minutes').format('hh:mm', {
    trim: false,
  });

  // Only navigatable to taskboard on these conditions
  const showLinkButton =
    task.taskLevel !== 2 &&
    (task.status !== 2 || task.deadline === null || moment.utc(task.deadline) > moment.utc());

  const changeStatusTitle = [
    messages.ChangeMainTaskStatus,
    messages.ChangeSubtaskStatus,
    messages.ChangeTodoStatus,
  ][task.taskLevel];

  const showAssignments = hasAssignments();

  return (
    <tbody className="hover">
      <tr>
        <td className="checkbox-column">
          <Checkbox onClick={() => handleTaskSelection(task)} value={task.selected} />
        </td>
        <td onClick={() => onShowTaskDetails(task)} className="text-clickable">
          <TaskIcon taskLevel={task.taskLevel} />

          {task.name && task.name}
          {!task.name && (
            <FormattedListValue
              list="tasktemplate"
              id={task.templateId}
              fallback={task.name || task.templateName || task.taskTemplateId}
            />
          )}
        </td>
        {columnStatus.assignedTo && (
          <td>
            <EditableUserSearch
              onChange={user => onUpdateUser({ ...task, userId: user?.id })}
              value={task?.userId}
              submitOnChange
              withUnassigned
            />
          </td>
        )}
        {columnStatus.relation && (
          <td>
            <RelationName
              relation={relation}
              extraIcons={
                task.missingAuthorizations?.length > 0 ? (
                  <TaskRequiredAuthorizationsIcon
                    taskId={task.id}
                    relationId={relation.id}
                    requiredAuthorizations={task.missingAuthorizations}
                  />
                ) : null
              }
            />
          </td>
        )}
        {columnStatus.customerCode && <td>{relation?.code}</td>}
        {showAssignments && columnStatus.assignment && (
          <td className="fw-normal">{mapInvoiceStreamName(task, intl, '')}</td>
        )}
        {columnStatus.status && (
          <td>
            <TaskStatusToggle
              id={`TaskStatusToggle-${task.id}`}
              value={task}
              onTasksUpdated={onTasksUpdated}
              isMainTask={task.taskLevel !== 1}
              title={changeStatusTitle ? intl.formatMessage(changeStatusTitle) : null}
              onOpenDetail={() => onShowTaskDetails(task)}
              onAddRegistration={() => onAddRegistrationClick(task)}
            />
          </td>
        )}
        {columnStatus.referenceYear && (
          <td className="text-center">{task.year === 0 ? null : task.year}</td>
        )}
        {columnStatus.executionDate && (
          <td className="text-center">
            {task.taskLevel !== 2 && (
              <EditableDatePicker
                showClearDate
                showTodayButton
                value={
                  task.executionDate
                    ? typeof task.executionDate === 'string'
                      ? moment.utc(task.executionDate)
                      : task.executionDate
                    : null
                }
                onChange={executionDate => onUpdateExecutionDate({ ...task, executionDate })}
              />
            )}
            {task.taskLevel === 2 && <div>&nbsp;</div>}
          </td>
        )}
        {columnStatus.executionDuration && (
          <td className="text-center">
            {task.taskLevel !== 2 && (
              <EditableNumber
                min={0}
                value={task.executionDuration}
                onChange={executionDuration =>
                  onUpdateExecutionDuration({ ...task, executionDuration })
                }
              />
            )}
            {task.taskLevel === 2 && <div>&nbsp;</div>}
          </td>
        )}
        {columnStatus.deadline && (
          <td className="text-center">
            <EditableDatePicker
              showClearDate
              value={typeof task.deadline === 'string' ? moment.utc(task.deadline) : task.deadline}
              onChange={deadline => onUpdateDeadline({ ...task, deadline })}
            />
          </td>
        )}
        {columnStatus.createdAt && (
          <td className="text-center fw-normal">
            {task.createdAt && <FormattedDateShort value={task.createdAt} showTimeInTooltip />}
          </td>
        )}
        {columnStatus.createdBy && <td className="fw-normal">{createdBy}</td>}

        {columnStatus.modifiedAt && (
          <td className="text-center fw-normal">
            {task.modifiedAt && <FormattedDateShort value={task.modifiedAt} showTimeInTooltip />}
          </td>
        )}
        {columnStatus.modifiedBy && <td className="fw-normal">{modifiedBy}</td>}
        {columnStatus.completedDate && (
          <td className="text-center fw-normal">
            {task.completedDate && (
              <FormattedDateShort value={task.completedDate} showTimeInTooltip />
            )}
          </td>
        )}
        {columnStatus.duration && <td className="text-center fw-normal">{formattedDuration}</td>}
        <td className="text-right actions-column">
          {task.type !== 2 && (
            <a
              className="btn btn-xs btn-icon"
              title={intl.formatMessage(messages.AddRegistration)}
              onClick={() => {
                onAddRegistrationClick(task);
              }}
            >
              <FaRegClock />
            </a>
          )}
          <a
            className={classNames('btn btn-xs btn-icon', {
              hide: !showLinkButton,
            })}
            onClick={() => {
              if (!showLinkButton) return;
              stateService.gotoTaskboard({
                relation,
                plannedTaskId: task.id,
              });
            }}
            title={intl.formatMessage(getLinkIconTooltipMessage(showLinkButton, task.taskLevel))}
          >
            <FaExternalLinkAlt />
          </a>
          <span className="margin4L">
            <TaskListActionsButton
              task={task}
              onOpenTask={onShowTaskDetails}
              onSendEmail={onSendEmail}
              onShareItem={onShareItem}
              onDeleteTask={onRequestDeleteTask}
            />
          </span>
        </td>
      </tr>
    </tbody>
  );
};

TaskListRow.propTypes = {
  task: PropTypes.shape({
    relation: PropTypes.object,
    id: PropTypes.string,
  }).isRequired,
  stateService: PropTypes.instanceOf(StateService).isRequired,
  intl: intlShape.isRequired,
  handleTaskSelection: PropTypes.func.isRequired,
  onRequestDeleteTask: PropTypes.func.isRequired,
  onSendEmail: PropTypes.func.isRequired,
  onShareItem: PropTypes.func.isRequired,
  onTasksUpdated: PropTypes.func.isRequired,
  columnStatus: PropTypes.shape({}).isRequired,
};

export default injectIntl(injectStateService(TaskListRow));
